import React from 'react';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import Select, { SingleValue, components } from 'react-select';
import { Popup } from 'semantic-ui-react';
import styled from '@emotion/styled';
import moment from 'moment';
import { read, utils } from 'xlsx';

import { ReactComponent as HelpIcon } from 'assets/icons/help.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as MinusCircleIcon } from 'assets/icons/minuscircle.svg';
import { ReactComponent as AddCircleIcon } from 'assets/icons/addcircle.svg';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import Button from 'common/button/button';
import { DATE_Of_MONTH } from 'constants/CONSTATNS';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
// import 'semantic-ui-css/semantic.min.css';
import EmployeeListAddModal, { IEmpFormInPayStatement } from '../employee-list-add-modal/employee-list-add-modal';
import ListOfEmployees from '../employee-specs-list.json';
import ListOfEmployeesResig from '../employee-specs-list-resig.json';
import useStoreStore from 'zustands/useStoreStore';
import { IEmpInPaystatement } from 'types/salary';
import { selectDateOfMonthArr } from 'constants/dataArr';
import { phoneNumberAddHypen } from 'constants/utils';
import CommonModal from 'common/CommonModal';
import DateSelector from 'common/date-selector/date-selector';
import styleGuide from 'constants/styleGuide';
import './employee-list-for-payroll.scss';
import { regExp_birth } from 'constants/regExp';

const BtnLabel = styled.label`
  display: flex;
  padding: 0px 15px 0px 15px;
  height: 50px;
  width: 100%;
  align-items: center;
  color: #666666;
  font-size: 16px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
`;
const RetireModalContainer = styled.div`
  min-width: 300px;
`;
const DateArea = styled.div`
  margin: 10% 0;
`;
const BtnArea = styled.div`
  display: flex;
  justify-content: center;
`;
const Btn = styled.button`
  padding: 5% 0;
  width: 80%;
  border: 1px solid ${styleGuide.palette.redColor};
  border-radius: 8px;
`;
const BtnText = styled.strong`
  color: ${styleGuide.palette.redColor};
`;

interface IEmpRetiring {
  empName: string;
  empSeq: number;
  entryDate: Date;
}

const SheetJSFT = ['xlsx', 'xlsb', 'xlsm', 'xls', 'xml', 'csv']
  .map(function (x) {
    return '.' + x;
  })
  .join(',');

const make_cols = (refstr: string) => {
  let o = [],
    C = utils.decode_range(refstr).e.c + 1;
  for (var i = 0; i < C; ++i) o[i] = { name: utils.encode_col(i), key: i };
  return o;
};

const EmployeeListForSalarySpecification: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { storeSeq } = useStoreStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [calDay, setCalDay] = React.useState<SingleValue<DATE_Of_MONTH>>({ value: '말일', label: '말일' }); // 급여정산일
  const [workingEmpList, setWorkingEmpList] = React.useState<IEmpInPaystatement[]>([]); // 근무 직원
  const [endEmpList, setEndEmpList] = React.useState<IEmpInPaystatement[]>([]); // 퇴사 직원
  const [visibleRetireModal, setVisibleRetireModal] = React.useState<boolean>(false); // 퇴사처리 모달
  const [retiringEmpInfo, setRetiringEmpInfo] = React.useState<IEmpInPaystatement>(); // 퇴사 or 퇴사취소 처리 중인 직원 데이터
  const [retiringDate, setRetiringDate] = React.useState<Date>(new Date()); // 퇴사처리 일자
  const [clickedEmpData, setClickedEmpData] = React.useState<IEmpInPaystatement>(); // 근무직원목록에서 직원 로우 클릭 시 저장 데이터
  const [employeeAddModal, setEmployeeAddModal] = React.useState<boolean>(false); // 직원 등록 모달

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getPaystubMember`, {
        STORE_SEQ: storeSeq,
      });

      if (data.resultCode === 0) {
        const { DATA } = data.resultData;
        if (DATA.length > 0) {
          setCalDay({ value: DATA[0].PAYDAY, label: DATA[0].PAYDAY === '말일' ? '말일' : DATA[0].PAYDAY + '일' });
          setWorkingEmpList(
            DATA.filter((i: IEmpInPaystatement) => i.RETIRE_DATE === '').map((i: IEmpInPaystatement) => {
              return {
                ...i,
                SS_NUMBER: Number(i.SS_NUMBER.substr(0, 2)) < 30 ? `20${i.SS_NUMBER}` : `19${i.SS_NUMBER}`,
                MOBILE_NO: phoneNumberAddHypen(i.MOBILE_NO),
                STANDARD_TAX: i.STANDARD_TAX === '0' ? '4대보험' : i.STANDARD_TAX === '1' ? '3.3%' : '없음',
                PAYDAY: i.PAYDAY === '말일' ? i.PAYDAY : `${i.PAYDAY}일`,
              };
            }),
          );
          setEndEmpList(
            DATA.filter((i: IEmpInPaystatement) => i.RETIRE_DATE !== '').map((i: IEmpInPaystatement) => {
              return {
                ...i,
                SS_NUMBER: Number(i.SS_NUMBER.substr(0, 2)) < 30 ? `20${i.SS_NUMBER}` : `19${i.SS_NUMBER}`,
                MOBILE_NO: phoneNumberAddHypen(i.MOBILE_NO),
                STANDARD_TAX: i.STANDARD_TAX === '0' ? '4대보험' : i.STANDARD_TAX === '1' ? '3.3%' : '없음',
                PAYDAY: `${i.PAYDAY}일`,
              };
            }),
          );
        }
      } else {
        throw new Error('/auth/getPaystubMember');
      }
    } catch (err) {
      checkErrorLog(err, 'employee-list-for-payroll /auth/getPaystubMember');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const handleSelectCalDay = async (data: SingleValue<DATE_Of_MONTH>) => {
    // try {
    //     setLoadingState({ isFetchLoading: true });
    //     const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updatePayday`, {
    //         PAYDAY: value
    //     })
    //     if (data.resultCode === 0) {
    //         setCalDay(data);
    //     } else {
    //         throw new Error('/auth/updatePayday')
    //     }
    // } catch (err){
    //     checkErrorLog(err, 'employee-list-for-payroll /auth/updatePayday');
    // } finally {
    //     setLoadingState({ isFetchLoading: false });
    // }
  };

  // 업로드 양식 엑셀 다운로드
  const DownloadExcel = () => {
    window.location.href =
      'https://wesop.s3.ap-northeast-2.amazonaws.com/shopsol/%E1%84%89%E1%85%A3%E1%86%B8%E1%84%89%E1%85%A9%E1%86%AF_%E1%84%80%E1%85%B3%E1%86%B8%E1%84%8B%E1%85%A7%E1%84%86%E1%85%A7%E1%86%BC%E1%84%89%E1%85%A6%E1%84%89%E1%85%A5_%E1%84%8C%E1%85%B5%E1%86%A8%E1%84%8B%E1%85%AF%E1%86%AB%E1%84%83%E1%85%B3%E1%86%BC%E1%84%85%E1%85%A9%E1%86%A8.xlsx';
  };

  // 직원 추가/수정 모달 표시 토글
  const handleEmpAddModal = () => {
    // 선택했던 직원정보 데이터 초기화
    if (employeeAddModal === true) {
      setClickedEmpData(undefined);
    }
    setEmployeeAddModal(!employeeAddModal);
  };

  // 엑셀 파일 업로드
  const FileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files[0]) return;

    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;
    reader.onload = (e) => {
      // parse data
      const bstr = e.target?.result;
      const wb = read(bstr, {
        type: rABS ? 'binary' : 'array',
        bookVBA: true,
      });
      // get first worksheet
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // Convert array of arrays
      const data: { [key: string]: string }[] = utils.sheet_to_json(ws, { range: 1 });
      // Update state
      if (ws['!ref']) {
        FormatExcelEmps(files[0], data, make_cols(ws['!ref']));
      }
    };

    if (rABS) {
      reader.readAsBinaryString(files[0]);
    } else {
      reader.readAsArrayBuffer(files[0]);
    }

    if (ExcelInputRef.current) ExcelInputRef.current.value = '';
  };

  const FormatExcelEmps = (file: File, data: { [key: string]: string }[], cols: { name: string; key: number }[]) => {
    let MissList: { id: number; name: string; content: string }[] = [];
    let EmpList: IEmpInPaystatement[] = [];

    if (data.length > 0) {
      data.map((item, idx) => {
        const NAME = item['직원명'] == undefined ? '미입력' : item['직원명'];
        if (item['직원명'] == '' || item['직원명'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '직원명이 입력되지 않았습니다.',
          });
        } else if (item['휴대폰번호'] == '' || item['휴대폰번호'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '휴대폰번호가 입력되지 않았습니다.',
          });
        } else if (item['생년월일'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '생년월일이 입력되지 않았습니다.',
          });
        } else if (!regExp_birth.test(item['생년월일'])) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '생년월일 형식이 올바르지 않습니다.',
          });
        } else if (item['휴대폰번호'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '휴대폰번호가 입력되지 않았습니다.',
          });
        } else if (item['휴대폰번호'].length < 11) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '휴대폰번호 형식이 올바르지 않습니다.',
          });
        } else if (isNaN(Number(item['휴대폰번호']))) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '휴대폰번호 형식이 올바르지 않습니다.',
          });
        }
        //  else if (item["급여지급일"] == undefined){
        //     MissList.push({ id: idx+3, name: NAME, content: '급여지급일이 입력되지 않았습니다.'})
        // } else if ((isNaN(Number(item["급여지급일"])) || Number(item["급여지급일"]) < 0 || Number(item["급여지급일"]) > 29) && item["급여지급일"] !== '말일' ){
        //     MissList.push({ id: idx+3, name: NAME, content: '급여지급일 형식이 올바르지 않습니다.'})
        // } else if (item["일일근무시간"] == undefined){
        //     MissList.push({ id: idx+3, name: NAME, content: '일일근무시간이 입력되지 않았습니다.'})
        // } else if (isNaN(Number(item["일일근무시간"])) || Number(item["일일근무시간"]) < 1 || Number(item["일일근무시간"]) > 24){
        //     MissList.push({ id: idx+3, name: NAME, content: '일일근무시간 형식이 올바르지 않습니다.'})
        // } else if (item["주근무일수"] == undefined){
        //     MissList.push({ id: idx+3, name: NAME, content: '주근무일수가 입력되지 않았습니다.'})
        // } else if (isNaN(Number(item["주근무일수"])) || Number(item["주근무일수"]) < 1 || Number(item["주근무일수"]) > 7){
        //     MissList.push({ id: idx+3, name: NAME, content: '주근무시간 형식이 올바르지 않습니다.'})
        // }
        else if (item['세금유형'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '세금유형이 입력되지 않았습니다.',
          });
        } else if (isNaN(Number(item['세금유형'])) || Number(item['세금유형']) < 0 || Number(item['세금유형']) > 7) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '세금유형 형식이 올바르지 않습니다.',
          });
        } else if (item['입사일'] == undefined) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '입사일이 입력되지 않았습니다.',
          });
        } else if (!moment(item['입사일']).isValid()) {
          MissList.push({
            id: idx + 3,
            name: NAME,
            content: '입사일 형식이 올바르지 않습니다.',
          });
        } else {
          EmpList.push({
            NAME: item['직원명'],
            SS_NUMBER: item['생년월일'],
            MOBILE_NO: item['휴대폰번호'],
            PAYDAY: '1',
            GENDER: '1',
            STANDARD_TAX: item['세금유형'],
            ENTRY_DATE: item['입사일'].split('-').join(''),
            RETIRE_DATE: '0000-00-00',
            DEPARTMENT: '',
            RANK: '',
            DEP_FAMILY: '1',
          });
        }
      });

      if (EmpList.length === 0) {
        setModalState({
          isModalShow: true,
          title: '급여명세서 직원등록',
          desc: '유효한 직원등록 정보가 없습니다. 다운로드 받은 양식을 사용하여 각 항목의 올바른 입력형식을 입력한 후 업로드해 주세요.',
        });
        return;
      }
      const missDesc = MissList.map((item) => {
        return `행번호: ${item.id} / 직원명: ${item.name} / ${item.content}`;
      });
      updateEmp(EmpList, '엑셀 업로드', missDesc);
    } else {
      setModalState({
        isModalShow: true,
        title: '급여명세서 직원등록',
        desc: '유효한 직원등록 정보가 없습니다. 다운로드 받은 양식을 사용하여 각 항목의 올바른 입력형식을 입력한 후 업로드해 주세요.',
      });
      return;
    }
  };

  // 직원 추가/수정 완료
  const updateEmp = (empData: IEmpInPaystatement[], insertType = '', missDescription?: string[]) => {
    try {
      setLoadingState({ isFetchLoading: true });

      const COMPLETE = async () => {
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/insertPaystubMember`, {
          STORE_SEQ: storeSeq,
          INSERT_FLAG: insertType === '등록' ? 'Y' : 'N',
          DATA: empData,
        });

        if (data.resultCode === 0) {
          if (data.resultData.message === 'SUCCESS') {
            setModalState({
              isModalShow: true,
              title: `급여명세서 직원${insertType}`,
              desc: `${insertType} 완료되었습니다.`,
            });
            if (insertType === '퇴사') {
              setRetiringDate(new Date());
              setVisibleRetireModal(false);
              setRetiringEmpInfo(undefined);
            }
            if (insertType === '등록' || insertType === '수정') {
              handleEmpAddModal();
            }
            fetchData();
          } else {
            setModalState({
              isModalShow: true,
              title: '급여명세서 직원등록',
              desc: '이미 등록된 직원입니다.',
            });
          }
        } else {
          throw new Error('/auth/insertPaystubMember');
        }
      };

      if (missDescription && missDescription.length > 0) {
        if (empData.length === 0) {
          return setModalState({
            isModalShow: true,
            title: '급여명세서 직원등록',
            desc: `해당 입력값이 올바르지 않습니다.\n\n${missDescription.join('\n')} 수정 후 다시 업로드해 주세요.`,
          });
        } else {
          return setModalState({
            isModalShow: true,
            title: '급여명세서 직원등록',
            desc: `입력값이 올바르지 않은 직원을 제외하고 등록합니다.\n\n${missDescription.join('\n')}`,
            okBtnText: '확인',
            okCallback: COMPLETE,
          });
        }
      } else {
        COMPLETE();
      }
    } catch (err) {
      checkErrorLog(err, 'employee-list-for-payroll /auth/insertPaystubMember');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 근무중 직원 퇴사처리 클릭
  const clickRetireEmp = (empData: IEmpInPaystatement) => {
    setRetiringEmpInfo(empData);
    if (empData.RETIRE_DATE !== '') {
      setRetiringDate(moment(empData.RETIRE_DATE).toDate());
    }
    setVisibleRetireModal(true);
  };
  // 퇴사처리 모달 닫기
  const closeRetireEmpModal = () => {
    setVisibleRetireModal(false);
    setRetiringEmpInfo(undefined);
  };
  // 퇴사처리 일자 변경
  const handleRetireEmpDate = (date: Date) => {
    setRetiringDate(date);
  };

  // 퇴사처리 완료
  const submitRetireEmp = async () => {
    let formatEmpData: IEmpInPaystatement[] = [];
    if (retiringEmpInfo) {
      formatEmpData = [
        {
          ...retiringEmpInfo,
          ENTRY_DATE: moment(retiringEmpInfo.ENTRY_DATE).format('YYYYMMDD'),
          RETIRE_DATE: moment(retiringDate).format('YYYYMMDD'),
          MOBILE_NO: retiringEmpInfo.MOBILE_NO.split('-').join(''),
          SS_NUMBER: moment(retiringEmpInfo.SS_NUMBER).format('YYMMDD'),
        },
      ];
    }
    updateEmp(formatEmpData, '퇴사');
  };

  // 퇴사직원 퇴사처리 취소 클릭
  const clickRetireCancel = (empData: IEmpInPaystatement) => {
    let formatEmpData: IEmpInPaystatement[] = [
      {
        ...empData,
        ENTRY_DATE: moment(empData.ENTRY_DATE).format('YYYYMMDD'),
        RETIRE_DATE: '',
        MOBILE_NO: empData.MOBILE_NO.split('-').join(''),
        SS_NUMBER: moment(empData.SS_NUMBER).format('YYMMDD'),
      },
    ];
    setModalState({
      isModalShow: true,
      title: `퇴사 취소`,
      desc: `${empData.NAME}직원의 퇴사를 취소하시겠습니까?`,
      okBtnText: '확인',
      okCallback: () => updateEmp(formatEmpData, '퇴사취소'),
    });
  };

  // 퇴사처리 직원 삭제하기
  const deletePayEmp = async (index?: string) => {
    try {
      if (!index) return null;
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/deletePaystubMember`, {
        MEMBER_INDEX: index,
      });

      if (data.resultCode === 0 && data.resultData.message === 'SUCCESS') {
        setModalState({
          isModalShow: true,
          title: `급여명세서 퇴사직원 삭제`,
          desc: `삭제 완료되었습니다.`,
        });
        fetchData();
      } else {
        throw new Error('/auth/deletePaystubMember');
      }
    } catch (err) {
      checkErrorLog(err, 'employee-list-for-payroll /auth/deletePaystubMember');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 퇴사처리 직원 삭제버튼 클릭
  const clickDeleteEmp = (empData: IEmpInPaystatement) => {
    setModalState({
      isModalShow: true,
      title: `급여명세서 직원 삭제`,
      desc: `${empData.NAME}직원을 삭제합니다`,
      okBtnText: '삭제',
      okCallback: () => deletePayEmp(empData.MEMBER_INDEX),
    });
  };

  // 직원 로우 클릭 시 수정 모달표시
  const clickEmpRow = (e: any, empData: IEmpInPaystatement) => {
    if (
      // 퇴사처리 컬럼 제외한 일반 로우 클릭 시에만 실행
      e?.target.localName !== 'svg' &&
      e?.target.localName !== 'path' &&
      e?.target?.className?.includes('td-content')
    ) {
      setClickedEmpData(empData);
      handleEmpAddModal();
    }
  };

  // 작성 및 전송 페이지 이동
  const enterPayStatementScreen = () => {
    navigate('/salary-specification-transfer-of-mgt');
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };
  const calDayArr = selectDateOfMonthArr();
  const ExcelInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="salary-specs-emp-list">
          <div className="salary-specs-emp-list__head">
            <div>
              <div className="salary-specs-emp-list__head__top">
                <div className="salary-specs-emp-list__head__nav">
                  급여명세서 <span className="salary-specs-emp-list__head__nav-span">{'>'}</span> 직원목록
                </div>
              </div>
              <div className="salary-specs-emp-list__head__heading">급여명세서 직원목록</div>

              <div className="salary-specs-emp-list__head__description">
                <div className="salary-specs-emp-list__head__description__head">
                  급여명세서를 보낼 직원을 등록하고 관리해 보세요.
                </div>
                <div className="salary-specs-emp-list__head__description__content">
                  - 직원을 등록하셨다면, '급여명세서 작성 및 전송'화면에서 카카오톡으로 직원에게 전송할 수 있습니다.
                </div>
                <div className="salary-specs-emp-list__head__description__content">
                  - 우측 상단의 전환 아이콘을 누르면 '급여명세서 작성 및 전송'화면으로 이동할 수 있습니다.
                </div>
                <div className="salary-specs-emp-list__head__description__content">
                  - 직원정보의 세금유형 변경 시 급여명세서를 다시 작성해 주세요.
                </div>
              </div>
              <div className="salary-specs-emp-list__body__head__dropdown">
                <Select
                  className="salary-specs-emp-list__body__head__item"
                  components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: () => (
                      <div style={{ paddingRight: '15px' }}>
                        <ArrowDownIcon />
                      </div>
                    ),
                    Control: ({ children, ...props }: any) => {
                      return (
                        <components.Control {...props}>
                          <div
                            style={{
                              paddingLeft: '10px',
                              fontFamily: 'Spoqa Han Sans Neo',
                              fontWeight: '500',
                              fontSize: '16px',
                              color: '#868686',
                            }}>
                            급여정산일
                          </div>
                          {children}
                        </components.Control>
                      );
                    },
                  }}
                  isSearchable={false}
                  value={calDay}
                  styles={colourStyles}
                  options={calDayArr}
                  onChange={handleSelectCalDay}
                />
              </div>
            </div>
            <div className="salary-specs-emp-list__head__button">
              <Button label="작성 및 전송" type="type2" height={50} action={enterPayStatementScreen} />
            </div>
          </div>

          <div className="salary-specs-emp-list__body">
            <div className="salary-specs-emp-list__body__reg-employee">
              <div className="salary-specs-emp-list__body__head">
                <div className="salary-specs-emp-list__body__head__heading">근무중 직원</div>
                <div className="salary-specs-emp-list__body__head__number">{workingEmpList.length}</div>
                <div className="salary-specs-emp-list__body__head__buttons">
                  <div className="salary-specs-emp-list__body__head__info">
                    엑셀로 직원 일괄 등록하는 법
                    <div className="salary-specs-emp-list__body__head__info__icon">
                      <Popup flowing={true} trigger={<HelpIcon />} position="bottom center">
                        <div className="salary-specs-emp-list__tooltip__head">직원 일괄등록</div>
                        <div className="salary-specs-emp-list__tooltip__body">
                          <div>1. ’엑셀 다운로드’ 버튼을 눌러 양식을 다운로드합니다.</div>
                          <div>2. 다운로드한 엑셀파일을 열어주세요.</div>
                          <div>3. 엑셀 상단에 적혀 있는 주의사항들을 참고하여 직원목륵을 작성해 주세요.</div>
                          <div className="salary-specs-emp-list__tooltip__highlight">
                            *형식에 맞지 않는 사람들은 등록 되지 않으니 꼭! 형식에 맞게 작성 부탁드립니다.
                          </div>
                          <div>{`4. [컨트롤 + S] 혹은 [파일> 저장] 으로 작성한 내용을 저장해 주세요.`}</div>
                          <div>5. 웹페이지로 돌아와 ‘엑셀 업로드' 버튼을 눌러주세요.</div>
                          <div>6. 저장한 엑셀파일을 찾은 뒤 등록하여 완료합니다</div>
                        </div>
                      </Popup>
                    </div>
                  </div>
                  <div className="salary-specs-emp-list__body__head__button">
                    <Button label="Excel 다운" type="type11" action={DownloadExcel} />
                  </div>

                  <div className="salary-specs-emp-list__body__head__button">
                    <input
                      ref={ExcelInputRef}
                      type="file"
                      id="SaveExcelEmpsBtn"
                      onChange={FileChange}
                      accept={SheetJSFT}
                      style={{ display: 'none' }}
                    />
                    <BtnLabel htmlFor="SaveExcelEmpsBtn">Excel 업로드</BtnLabel>
                  </div>

                  <div className="salary-specs-emp-list__body__head__button" onClick={handleEmpAddModal}>
                    <Button label="+ 직원등록" type="type12" />
                  </div>
                </div>
              </div>
              <table className="salary-specs-emp-list__body__table-reg">
                <tr className="table-reg__tr">
                  <th className="salary-specs-emp-list__body__table-reg__td">no.</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">이름</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">생년월일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">핸드폰 번호</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">입사일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">세금유형</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">급여정산일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">부서</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">직급</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">부양가족</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">퇴사처리</th>
                </tr>
                {workingEmpList.length > 0 &&
                  workingEmpList.map((employee: IEmpInPaystatement, index: number) => (
                    <tr key={employee.MEMBER_INDEX} className="table-reg__tr" onClick={(e) => clickEmpRow(e, employee)}>
                      <td className="salary-specs-emp-list__body__table-reg__td-center">{index}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.NAME}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">
                        {moment(employee.SS_NUMBER).format('YYMMDD')}
                      </td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.MOBILE_NO}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.ENTRY_DATE}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.STANDARD_TAX}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.PAYDAY}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.DEPARTMENT}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.RANK}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.DEP_FAMILY}</td>
                      <td
                        className="table-reg__td-center salary-specs-emp-list__clickable"
                        onClick={() => clickRetireEmp(employee)}>
                        <MinusCircleIcon className="" />
                      </td>
                    </tr>
                  ))}
              </table>
            </div>

            <div className="salary-specs-emp-list__body__reg-employee-2">
              <div className="salary-specs-emp-list__body__head">
                <div className="salary-specs-emp-list__body__head__heading">퇴사처리 직원</div>
                <div className="salary-specs-emp-list__body__head__number-del">{endEmpList.length}</div>
              </div>
              <table className="salary-specs-emp-list__body__table-reg">
                <tr className="table-reg__tr">
                  <th className="salary-specs-emp-list__body__table-reg__td">no.</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">이름</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">생년월일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">핸드폰 번호</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">입사일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">퇴사일</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">퇴사취소</th>
                  <th className="salary-specs-emp-list__body__table-reg__td">직원삭제</th>
                </tr>
                {endEmpList.length > 0 &&
                  endEmpList.map((employee: IEmpInPaystatement, index: number) => (
                    <tr key={employee.MEMBER_INDEX} className="table-reg__tr">
                      <td className="salary-specs-emp-list__body__table-reg__td-center">{index}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.NAME}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">
                        {moment(employee.SS_NUMBER).format('YYMMDD')}
                      </td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.MOBILE_NO}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.ENTRY_DATE}</td>
                      <td className="salary-specs-emp-list__body__table-reg__td-content">{employee.RETIRE_DATE}</td>
                      <td
                        className="table-reg__td-center salary-specs-emp-list__clickable"
                        onClick={() => {
                          clickRetireCancel(employee);
                        }}>
                        <AddCircleIcon />
                      </td>
                      <td
                        className="salary-specs-emp-list__body__table-reg__td-content salary-specs-emp-list__clickable"
                        onClick={() => clickDeleteEmp(employee)}>
                        삭제
                      </td>
                    </tr>
                  ))}
              </table>
            </div>
          </div>
        </div>
      </WithSideBarAndNavBar>
      <EmployeeListAddModal
        isVisible={employeeAddModal}
        empData={clickedEmpData}
        onClose={handleEmpAddModal}
        onSubmit={updateEmp}
      />
      <CommonModal
        isVisible={visibleRetireModal}
        onClose={closeRetireEmpModal}
        title={`${retiringEmpInfo?.NAME ?? ''}직원 퇴사 처리`}>
        <RetireModalContainer>
          <DateArea>
            <DateSelector
              onChange={handleRetireEmpDate}
              value={retiringDate}
              minDate={moment(retiringEmpInfo?.ENTRY_DATE).toDate()}
            />
          </DateArea>
          <BtnArea>
            <Btn onClick={submitRetireEmp}>
              <BtnText>완료</BtnText>
            </Btn>
          </BtnArea>
        </RetireModalContainer>
      </CommonModal>
    </>
  );
};

export default EmployeeListForSalarySpecification;
